import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Box3 } from 'three';

import { isValidNumber } from '../../../../../../utils/math/number';
import { buildOptionsArray, isDefined } from '../../../../../../utils/variables';
import { AssetVisibilityManager } from '../../../../../../viewer/asset-visibility-manager';
import cameraManager from '../../../../../../viewer/camera-manager';
import { Dxf } from '../../../../../../viewer/elements/dxf';
import { ElementObj } from '../../../../../../viewer/elements/element-obj';
import { Glb } from '../../../../../../viewer/elements/glb';
import { InputHandler } from '../../../../../../viewer/input-handler';
import { SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { ReferencePointTransformTool } from '../../../../../../viewer/tools/reference-point-transform-tool';
import {
  Coordinates,
  coordinateToPoint3d,
  Point2d,
  toVector2,
} from '../../../../../../viewer/utilities/math-helpers';
import { useNonce } from '../../../../../hooks/use-nonce';
import { useSubscribe } from '../../../../../hooks/use-subscribe';
import { Button } from '../../../../button/button';
import { CheckBox } from '../../../../check-box/check-box';
import { Icon } from '../../../../icon/icon';
import { InfoBox } from '../../../../info-box/info-box';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { List } from '../../../../list/list';
import { OverlayLoader } from '../../../../overlay-loader/overlay-loader';
import { Slider } from '../../../../slider/slider';
import { Stack } from '../../../../stack/stack';
import { TextBox } from '../../../../text-box/text-box';
import { SkyviewContext } from '../../../skyview-state';
import { CollapsableSection } from '../../components/collapsable-section';
import { CollapsableSectionGroup } from '../../components/collapsable-section-group';
import { RightPanelHeader } from '../../components/right-menu-header';
import { RightPanelContent, RightPanelFooter } from '../../components/styles';
import { KnownCooridnatesSection } from './sections/known-coordinates-section';
import { ModeSection } from './sections/mode-section';
import { ReferenceLengthSection } from './sections/reference-length-section';
import { ReferencePointSection } from './sections/reference-point-section';
import { ToolStartedPanel } from './tool-started-panel';

type Props = { tool: ReferencePointTransformTool };

export type ReferencePointTransformMode = 'coordinates' | 'length';

type State = 'idle' | 'error' | 'loading' | 'success';

export function canAssetBeTransformedWithReferencePointTool(
  asset?: ElementObj,
): asset is Glb | Dxf {
  return asset instanceof Dxf || (asset instanceof Glb && asset.canBeTransformed());
}

function getDefaultCoordinate(): Coordinates {
  return { northing: 0, easting: 0, height: 0 };
}

const ReferencePointTransform = (props: Props) => {
  const { t } = useTranslation();
  const [state, setState] = React.useState<State>('idle');
  const { selectedAsset } = React.useContext(SkyviewContext);
  const [asset, setAsset] = React.useState<Glb | Dxf | undefined>(
    canAssetBeTransformedWithReferencePointTool(selectedAsset) ? selectedAsset : undefined,
  );
  const [assetLoaded, setAssetLoaded] = useState(false);
  const [autoUpdatePreview, setAutoUpdatePreview] = useState(props.tool.autoUpdatePreview);
  const [scale, setScale] = useState(props.tool.targetScale.toString());
  const [optionsSectionExpanded, setOptionsSectionExpanded] = useState(true);
  const [mode, setMode] = React.useState<ReferencePointTransformMode>(props.tool.mode);

  const [referencePointA, setReferencePointA] = React.useState<Coordinates>(getDefaultCoordinate());
  const [referencePointB, setReferencePointB] = React.useState<Coordinates>(getDefaultCoordinate());

  const [referenceLength, setReferenceLength] = React.useState(props.tool.referenceLength ?? 1);
  const [referenceLengthValid, setReferenceLengthValid] = React.useState(false);

  const [knownCoordinateA, setKnownCoordinateA] = React.useState<Coordinates>(
    props.tool.knownCoordinateA ?? getDefaultCoordinate(),
  );
  const [knownCoordinateB, setKnownCoordinateB] = React.useState<Coordinates>(
    props.tool.knownCoordinateB ?? getDefaultCoordinate(),
  );
  const [knownCoordinatesValid, setKnownCoordinatesValid] = React.useState(false);
  const [previewOpacity, setPreviewOpacity] = useState(1);

  const showCustomScale = asset instanceof Dxf;

  const onSetKnownCoordinateA = (value: Coordinates) => {
    setState('idle');
    setKnownCoordinateA(value);
    props.tool.knownCoordinateA = value;
  };

  const onSetKnownCoordinateB = (value: Coordinates) => {
    setKnownCoordinateB(value);
    props.tool.knownCoordinateB = value;
  };

  const [refreshCoordinates, setRefreshCoordinates] = useNonce();

  useSubscribe(SubscriptionTopic.OnReferencePointTransformCoordinateASet, (coords) => {
    onSetKnownCoordinateA(coords);
    setRefreshCoordinates();
  });
  useSubscribe(SubscriptionTopic.OnReferencePointTransformCoordinateBSet, (coords) => {
    onSetKnownCoordinateB(coords);
    setRefreshCoordinates();
  });
  useSubscribe(SubscriptionTopic.AssetChanged, (message) => {
    if (isDefined(asset) && asset.uuid === message.uuid && message.loadingStatus === 'loaded') {
      setAssetLoaded(true);
    }
  });

  const onSetReferenceLength = (value: number) => {
    setState('idle');
    setReferenceLength(value);
    props.tool.referenceLength = value;
  };

  const pointsTooClose = (a: Point2d, b: Point2d) => toVector2(a).distanceTo(toVector2(b)) < 0.02;

  const referencePointsDefined = isDefined(referencePointA) && isDefined(referencePointB);
  const referencePointsTooClose = referencePointsDefined
    ? pointsTooClose(coordinateToPoint3d(referencePointA), coordinateToPoint3d(referencePointB))
    : true;

  const referencePointsValid = referencePointsDefined && !referencePointsTooClose;

  const knownCoordinatesTooClose = pointsTooClose(
    coordinateToPoint3d(knownCoordinateA),
    coordinateToPoint3d(knownCoordinateB),
  );

  const isAssetCompatibleWithMode = mode === 'length' ? asset?.canBeScaled() : true;
  const inputValid =
    isAssetCompatibleWithMode &&
    (mode === 'coordinates'
      ? knownCoordinatesValid && !knownCoordinatesTooClose
      : referenceLengthValid);

  const showReferencePointsTooClose = referencePointsDefined && referencePointsTooClose;
  const showKnownCoordinatesInvalid = mode === 'coordinates' && !knownCoordinatesValid;
  const showKnownCoordinatesTooClose =
    mode === 'coordinates' && knownCoordinatesValid && knownCoordinatesTooClose;
  const showReferenceLengthInvalid = mode === 'length' && !referenceLengthValid;

  const inputInfo = buildOptionsArray(
    [!referencePointsDefined, t('validation.referencePointsNotDefined', { ns: 'skyviewScale' })],
    [showReferencePointsTooClose, t('validation.referencePointsTooClose', { ns: 'skyviewScale' })],
    [showKnownCoordinatesInvalid, t('validation.knownCoordinatesInvalid', { ns: 'skyviewScale' })],
    [
      showKnownCoordinatesTooClose,
      t('validation.knownCoordinatesTooClose', { ns: 'skyviewScale' }),
    ],
    [
      !isAssetCompatibleWithMode,
      t('validation.assetNotCompatibleWithReferenceLength', { ns: 'skyviewScale' }),
    ],
    [showReferenceLengthInvalid, t('validation.referenceLengthInvalid', { ns: 'skyviewScale' })],
  );

  const disableConfirmButton = !isDefined(asset) || !referencePointsValid || !inputValid;

  useEffect(() => {
    props.tool.mode = mode;
    if (mode !== 'coordinates') {
      setAutoUpdatePreview(false);
    }
  }, [mode, props.tool]);

  useEffect(() => {
    props.tool.autoUpdatePreview = autoUpdatePreview;
  }, [autoUpdatePreview, props.tool]);

  useEffect(() => {
    props.tool.onReferencePointAChanged = (newValue: Coordinates) => {
      setState('idle');
      setReferencePointA(newValue);
    };
    props.tool.onReferencePointBChanged = (newValue: Coordinates) => {
      setState('idle');
      setReferencePointB(newValue);
    };

    return () => {
      props.tool.onReferencePointAChanged = undefined;
      props.tool.onReferencePointBChanged = undefined;
    };
  }, [props.tool]);

  useEffect(() => {
    setState('idle');

    if (canAssetBeTransformedWithReferencePointTool(selectedAsset)) {
      setAssetLoaded(selectedAsset.loadingStatus === 'loaded');
      setAsset(selectedAsset);
    } else {
      setReferencePointA(getDefaultCoordinate());
      setReferencePointB(getDefaultCoordinate());
      setPreviewOpacity(1);
      setAssetLoaded(false);
      setAsset(undefined);
    }
  }, [props.tool, selectedAsset]);

  useEffect(() => {
    // Reset tool whenever asset or asset visible changes.
    props.tool.reset();

    // Init the tool only when asset is set and loaded.
    if (asset && assetLoaded && props.tool.asset !== asset) {
      setPreviewOpacity(1);
      props.tool.onInit(asset);
      // Hide asset after tool has been initiated.
      void AssetVisibilityManager.getInstance().toggleAsset(asset.uuid, false, false);
    }

    // Toggle asset if it's not loaded or loading already.
    if (asset && !assetLoaded && asset.loadingStatus === 'idle') {
      void AssetVisibilityManager.getInstance().toggleAsset(asset.uuid, true, false);
    }
  }, [props.tool, asset, assetLoaded]);

  if (!isDefined(asset)) {
    return (
      <Component spacing={0}>
        <RightPanelHeader
          icon={['fal', 'arrows-maximize']}
          name={t('initial.title', { ns: 'skyviewScale' })}
        />
        <ToolStartedPanel />
      </Component>
    );
  }

  const renderInfoBoxes = () => {
    switch (state) {
      case 'idle':
        if (disableConfirmButton) {
          return (
            <InfoBox color="yellow">
              <List items={inputInfo} marker="disc" useMarkerForSingleItem={false} />
            </InfoBox>
          );
        }
        return;
      case 'error':
        return <InfoBox color="red">{t('unexpectedError', { ns: 'common' })}</InfoBox>;
      case 'success':
        return <InfoBox color="green">{t('success', { ns: 'skyviewScale' })}</InfoBox>;
    }
  };

  return (
    <Component spacing={0}>
      <RightPanelHeader icon={['fal', 'arrows-maximize']} name={asset.name}>
        <Icon
          disabled={!isDefined(props.tool.preview)}
          fixedWidth={true}
          icon={['fad', 'square']}
          title={t('focus', { ns: 'common' })}
          onClick={() => {
            if (props.tool.preview) {
              cameraManager()
                .getActiveController()
                ?.focusOnBox(new Box3().setFromObject(props.tool.preview));
            }
          }}
          onHoverStyle={{ icon: ['fas', 'square'] }}
        />
      </RightPanelHeader>

      <RightPanelContent useTopPadding={false}>
        <CollapsableSectionGroup>
          <OverlayLoader visible={!assetLoaded || state === 'loading'}>
            <CollapsableSection
              expanded={optionsSectionExpanded}
              title={t('options', { ns: 'common' })}
              onSetExpanded={(state) => setOptionsSectionExpanded(state)}
            >
              <Stack spacing={1}>
                <LabelledContainer text={t('options.opacitySlider', { ns: 'skyviewScale' })}>
                  <Slider
                    max={1}
                    min={0}
                    step={0.01}
                    value={previewOpacity}
                    onChange={(value) => {
                      setPreviewOpacity(value);
                      props.tool.setPreviewOpacity(value);
                    }}
                  />
                </LabelledContainer>

                {
                  <CheckBox
                    checked={autoUpdatePreview}
                    disabled={mode !== 'coordinates'}
                    onChange={() => setAutoUpdatePreview((oldValue) => !oldValue)}
                  >
                    {t('options.autoUpdatePreview', { ns: 'skyviewScale' })}
                  </CheckBox>
                }

                {showCustomScale && (
                  <LabelledContainer
                    infoTooltip={t('options.scaleFactorInfo', { ns: 'skyviewScale' })}
                    text={t('options.scaleFactor', { ns: 'skyviewScale' })}
                  >
                    <TextBox
                      disableScroll={true}
                      min={0}
                      placeholder={t('options.autoScale', { ns: 'skyviewScale' })}
                      type="number"
                      value={scale}
                      onChange={(e) => {
                        if (isValidNumber(e.target.value)) {
                          props.tool.targetScale = Number(e.target.value);
                        } else {
                          props.tool.targetScale = 0;
                        }

                        setScale(e.target.value);
                      }}
                    />
                  </LabelledContainer>
                )}
              </Stack>
            </CollapsableSection>

            <ModeSection mode={mode} setMode={setMode} />
            <ReferencePointSection coordinateA={referencePointA} coordinateB={referencePointB} />

            {mode === 'coordinates' && (
              <KnownCooridnatesSection
                key={refreshCoordinates}
                knownCoordinateA={knownCoordinateA}
                knownCoordinateB={knownCoordinateB}
                setKnownCoordinateA={onSetKnownCoordinateA}
                setKnownCoordinateB={onSetKnownCoordinateB}
                setValid={setKnownCoordinatesValid}
              />
            )}

            {mode === 'length' && (
              <ReferenceLengthSection
                referenceLength={referenceLength}
                setReferenceLength={onSetReferenceLength}
                setValid={setReferenceLengthValid}
              />
            )}
          </OverlayLoader>
        </CollapsableSectionGroup>

        {renderInfoBoxes()}
      </RightPanelContent>

      <RightPanelFooter>
        <Stack direction="column" spacing={0.5}>
          <Button
            color="secondary"
            disabled={mode !== 'coordinates' || disableConfirmButton || state === 'loading'}
            leftIcon={{ icon: ['fad', 'magnifying-glass'] }}
            loading={state === 'loading'}
            variant="contained"
            width="100%"
            onClick={() => {
              props.tool.calculatePreviewTransformation();
            }}
          >
            {t('options.updatePreview', { ns: 'skyviewScale' })}
          </Button>

          <Button
            color="primary"
            disabled={disableConfirmButton || state === 'loading'}
            loading={state === 'loading'}
            variant="contained"
            width="100%"
            onClick={async () => {
              setState('loading');
              try {
                if (asset instanceof Dxf) {
                  await props.tool.createDxfExport();
                } else if (mode === 'coordinates') {
                  await props.tool.scaleFromknownCoordinates(knownCoordinateA, knownCoordinateB);
                } else {
                  await props.tool.scaleFromReferenceLength(Number(referenceLength));
                }

                InputHandler.instance.setTool('select');
                setState('success');
              } catch {
                setState('error');
              } finally {
                // Reset the preview opacity after the transformation has finished.
                props.tool.setPreviewOpacity(previewOpacity);
              }
            }}
          >
            {t('confirm', { ns: 'common' })}
          </Button>
        </Stack>
      </RightPanelFooter>
    </Component>
  );
};

const Component = styled(Stack)`
  height: 100%;
`;

export { ReferencePointTransform };
